/**
 * Installation Job Detail Page
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  User,
  Phone,
  Mail,
  Key,
  ClipboardList,
  FileText,
  History,
  Trash2,
  Copy,
  RefreshCw,
  Pencil,
  Send,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSession } from '@/hooks/useSession';
import {
  useInstallationJob,
  useDeleteInstallationJob,
  useGenerateAccessCode,
  useSendAccessCode,
  useDispatchToFieldNation,
  useQuoteByMongoId,
} from '../hooks/useInstallationJobs';
import { JobStatusBadge } from '../components/JobStatusBadge';
import { AccessCodeCard } from '../components/AccessCodeCard';
import { ChecklistSection } from '../components/ChecklistSection';
import { AuditLogSection } from '../components/AuditLogSection';
import { EditJobDialog } from '../components/EditJobDialog';
import { ApproveJobDialog } from '../components/ApproveJobDialog';
import { RejectJobDialog } from '../components/RejectJobDialog';
import { SensorManagementSection } from '../components/SensorManagementSection';
import { ChecklistTemplateSection } from '../components/ChecklistTemplateSection';
import { InstallationIqSection } from '../components/InstallationIqSection';
import { canReviewInstallationIq } from '../permissions';
import { shouldShowIqReview } from './installationIqVisibility';

export default function InstallationJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const [activeTab, setActiveTab] = useState('overview');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<{ code: string; id: string } | null>(null);
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
  const [sendEmailRecipient, setSendEmailRecipient] = useState('');
  const [isDispatchDialogOpen, setIsDispatchDialogOpen] = useState(false);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  const { data: job, isLoading, isError, refetch } = useInstallationJob(id);
  const { data: linkedQuote } = useQuoteByMongoId(job?.mongoQuoteId);
  const deleteJob = useDeleteInstallationJob();
  const generateCode = useGenerateAccessCode();
  const sendCode = useSendAccessCode();
  const dispatchToFN = useDispatchToFieldNation();
  const canGenerateAccessCode = ['scheduled', 'in_progress', 'rejected'].includes(job?.status ?? '');

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteJob.mutateAsync(id);
      toast.success('Job deleted successfully');
      navigate('/operations/installations');
    } catch (error) {
      toast.error('Failed to delete job');
    }
    setIsDeleteDialogOpen(false);
  };

  const handleGenerateCode = async () => {
    if (!id) return;
    try {
      const result = await generateCode.mutateAsync({ jobId: id, expiresInDays: 7 });
      setGeneratedCode({ code: result.code, id: result.id });
      toast.success('Access code generated');
    } catch (error) {
      toast.error('Failed to generate access code');
    }
  };

  const handleSendEmail = async () => {
    if (!id || !generatedCode || !sendEmailRecipient.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      await sendCode.mutateAsync({
        jobId: id,
        codeId: generatedCode.id,
        method: 'email',
        recipient: sendEmailRecipient.trim(),
        code: generatedCode.code,
      });
      toast.success('Access code sent via email');
      setIsSendEmailDialogOpen(false);
      setSendEmailRecipient('');
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-CA');
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-CA');
  };

  const handleDispatchToFieldNation = async () => {
    if (!id) return;
    try {
      const result = await dispatchToFN.mutateAsync(id);
      toast.success('Job dispatched to Field Nation', {
        description: `Work Order ID: ${result.workOrderId}`,
        action: {
          label: 'View',
          onClick: () => window.open(result.fieldNationUrl, '_blank'),
        },
      });
      setIsDispatchDialogOpen(false);
    } catch (error) {
      toast.error('Failed to dispatch to Field Nation');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/operations/installations">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
        </div>
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Failed to load installation job. Please try again.
          </CardContent>
        </Card>
      </div>
    );
  }

  const showIqReview = shouldShowIqReview(job, canReviewInstallationIq(user));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to="/operations/installations">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{job.jobNumber}</h1>
              <JobStatusBadge status={job.status} />
            </div>
            <p className="text-muted-foreground">{job.title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Primary review actions — prominent when pending_review */}
          {job.status === 'pending_review' && (
            <>
              <Button
                variant="outline"
                onClick={() => setIsRejectDialogOpen(true)}
                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button
                onClick={() => setIsApproveDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve & Complete
              </Button>
            </>
          )}

          {/* Dispatch — shown as button only when not in review */}
          {!job.fieldNationWorkOrderId && job.status !== 'completed' && job.status !== 'cancelled' && job.status !== 'pending_review' && (
            <Button
              onClick={() => setIsDispatchDialogOpen(true)}
              className="bg-[#f26722] hover:bg-[#d85a1c] text-white"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Dispatch to Field Nation
            </Button>
          )}

          {/* Secondary actions dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">More actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              {!job.fieldNationWorkOrderId && job.status !== 'completed' && job.status !== 'cancelled' && job.status === 'pending_review' && (
                <DropdownMenuItem onClick={() => setIsDispatchDialogOpen(true)}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Dispatch to Field Nation
                </DropdownMenuItem>
              )}
              {job.fieldNationWorkOrderId && job.fieldNationUrl && (
                <DropdownMenuItem onClick={() => window.open(job.fieldNationUrl, '_blank')}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View in Field Nation
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Generated Code Alert */}
      {generatedCode && (
        <Card className="border-green-500 bg-green-50 dark:bg-green-950">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">
                  Access Code Generated
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Share this code with the technician. It will only be shown once.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <code className="px-3 py-2 bg-white dark:bg-gray-800 rounded text-lg font-mono font-bold">
                  {generatedCode.code}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(generatedCode.code)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSendEmailDialogOpen(true)}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Send Email
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setGeneratedCode(null)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <FileText className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="checklist">
            <ClipboardList className="mr-2 h-4 w-4" />
            Checklist
          </TabsTrigger>
          <TabsTrigger value="access-codes">
            <Key className="mr-2 h-4 w-4" />
            Access Codes
          </TabsTrigger>
          <TabsTrigger value="audit-log">
            <History className="mr-2 h-4 w-4" />
            Audit Log
          </TabsTrigger>
          {showIqReview && (
            <TabsTrigger value="iq">
              <CheckCircle className="mr-2 h-4 w-4" />
              IQ
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Job Details */}
            <Card>
              <CardHeader>
                <CardTitle>Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {job.description && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Description</p>
                    <p>{job.description}</p>
                  </div>
                )}
                {job.instructions && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Instructions</p>
                    <p className="whitespace-pre-wrap">{job.instructions}</p>
                  </div>
                )}
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Created</p>
                    <p>{formatDateTime(job.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Last Updated</p>
                    <p>{formatDateTime(job.updatedAt)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Quote</p>
                    {job.mongoQuoteId && linkedQuote && job.accountId ? (
                      <Link to={`/accounts/${job.accountId}?tab=quotes`} className="text-primary hover:underline font-medium">
                        {linkedQuote.quoteNumber}
                      </Link>
                    ) : job.mongoQuoteId && linkedQuote ? (
                      <span className="font-medium">{linkedQuote.quoteNumber}</span>
                    ) : job.mongoQuoteId ? (
                      <span className="text-sm text-muted-foreground">Loading...</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">No quote linked</span>
                    )}
                    {linkedQuote?.projectName && (
                      <p className="text-sm text-muted-foreground">{linkedQuote.projectName}</p>
                    )}
                  </div>
                  {job.completedAt && (
                    <>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Completed</p>
                        <p>{formatDateTime(job.completedAt)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Completed By</p>
                        <p>{job.completedByName || '-'}</p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Schedule & Location */}
            <Card>
              <CardHeader>
                <CardTitle>Schedule & Location</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Account */}
                {job.accountName && (
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Account</p>
                      {job.accountId ? (
                        <Link to={`/accounts/${job.accountId}`} className="font-medium text-primary hover:underline">
                          {job.accountName}
                        </Link>
                      ) : (
                        <p className="font-medium">{job.accountName}</p>
                      )}
                      {job.accountNumber && (
                        <p className="text-sm text-muted-foreground">{job.accountNumber}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Site */}
                {(job.siteName || job.address || job.city || job.stateProvince || job.postalCode) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{job.siteName ? 'Site' : 'Installation Address'}</p>
                      {job.siteName && <p className="font-medium">{job.siteName}</p>}
                      {job.address && <p className="text-sm whitespace-pre-line">{job.address}</p>}
                      {(job.city || job.stateProvince || job.postalCode) && (
                        <p className="text-sm text-muted-foreground">
                          {[job.city, job.stateProvince, job.postalCode].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Schedule */}
                {job.scheduledDate && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Scheduled Date</p>
                      <p className="font-medium">{formatDate(job.scheduledDate)}</p>
                      {job.scheduledTimeStart && (
                        <p className="text-sm text-muted-foreground">
                          {job.scheduledTimeStart}
                          {job.scheduledTimeEnd && ` - ${job.scheduledTimeEnd}`}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Contact */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Contact</p>
                  {job.contactName && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-muted-foreground" />
                      {job.contactName}
                    </div>
                  )}
                  {job.contactPhone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      {job.contactPhone}
                    </div>
                  )}
                  {job.contactEmail && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      {job.contactEmail}
                    </div>
                  )}
                  {!job.contactName && !job.contactPhone && !job.contactEmail && (
                    <p className="text-sm text-muted-foreground">No contact information</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Field Nation Integration */}
            {job.fieldNationWorkOrderId && job.fieldNationUrl && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Field Nation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Work Order ID</p>
                      <p className="font-mono">{job.fieldNationWorkOrderId}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Status</p>
                      <Badge variant="outline" className="capitalize">
                        {job.fieldNationStatus?.replace('_', ' ') || 'Pending'}
                      </Badge>
                    </div>
                    {job.dispatchedToFieldNationAt && (
                      <div className="col-span-2">
                        <p className="text-sm font-medium text-muted-foreground">Dispatched At</p>
                        <p>{formatDateTime(job.dispatchedToFieldNationAt)}</p>
                      </div>
                    )}
                  </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.open(job.fieldNationUrl, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in Field Nation
                </Button>
                </CardContent>
              </Card>
            )}

            {/* Sensors */}
            <SensorManagementSection job={job} />

            {/* Checklist Template */}
            <ChecklistTemplateSection job={job} />

            {/* Transmitters */}
            {job.transmitters.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Transmitters ({job.transmitters.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {job.transmitters.map((tx, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 rounded border"
                      >
                        <div>
                          <p className="font-medium">{tx.transmitterName}</p>
                          <p className="text-sm text-muted-foreground font-mono">{tx.devId}</p>
                        </div>
                        <Badge variant={tx.installed ? 'success' : 'secondary'}>
                          {tx.installed ? 'Installed' : 'Pending'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="checklist">
          <ChecklistSection job={job} />
        </TabsContent>

        <TabsContent value="access-codes" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Access Codes</CardTitle>
                <CardDescription>
                  Generate and manage access codes for technicians
                </CardDescription>
              </div>
              <Button onClick={handleGenerateCode} disabled={generateCode.isPending || !canGenerateAccessCode}>
                <Key className="mr-2 h-4 w-4" />
                {generateCode.isPending ? 'Generating...' : 'Generate Code'}
              </Button>
            </CardHeader>
            <CardContent>
              {!['scheduled', 'in_progress', 'rejected'].includes(job.status) && (
                <div className="flex items-start gap-3 p-3 mb-4 rounded-md border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                  <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">Job is not scheduled</p>
                    <p>Technicians can only use access codes when the job status is <strong>Scheduled</strong>, <strong>In Progress</strong>, or <strong>Rejected</strong>. The current status is <strong className="capitalize">{job.status.replace('_', ' ')}</strong>.</p>
                  </div>
                </div>
              )}
              {job.accessCodes && job.accessCodes.length > 0 ? (
                <div className="space-y-4">
                  {job.accessCodes.map((code) => (
                    <AccessCodeCard key={code.id} code={code} jobId={job.id} />
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No access codes generated yet. Generate a code to allow technicians to access this job.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit-log">
          <AuditLogSection jobId={job.id} />
        </TabsContent>

        {showIqReview && (
          <TabsContent value="iq">
            <InstallationIqSection
              job={job}
              linkedQuoteNumber={linkedQuote?.quoteNumber ?? null}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Installation Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete job {job.jobNumber}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Job Dialog */}
      <EditJobDialog job={job} open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} />
      <ApproveJobDialog job={job} open={isApproveDialogOpen} onOpenChange={setIsApproveDialogOpen} />
      <RejectJobDialog job={job} open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen} />

      {/* Send Email Dialog */}
      <Dialog open={isSendEmailDialogOpen} onOpenChange={setIsSendEmailDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Send Access Code via Email</DialogTitle>
            <DialogDescription>
              Send the access code to the technician's email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="technician@example.com"
                value={sendEmailRecipient}
                onChange={(e) => setSendEmailRecipient(e.target.value)}
              />
            </div>
            {generatedCode && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground mb-1">Code to send:</p>
                <code className="text-lg font-mono font-bold">{generatedCode.code}</code>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSendEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendCode.isPending}>
              {sendCode.isPending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch to Field Nation Dialog */}
      <AlertDialog open={isDispatchDialogOpen} onOpenChange={setIsDispatchDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dispatch to Field Nation</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a work order in Field Nation for job {job.jobNumber}. The job details,
              location, schedule, and contact information will be sent to Field Nation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDispatchToFieldNation}
              disabled={dispatchToFN.isPending}
            >
              {dispatchToFN.isPending ? 'Dispatching...' : 'Dispatch'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
